import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { parseServerUtc } from '../../utils/server-time';

// What the server says when it has sent a code: where it went (masked), when it stops working, and how long to wait before another.
export type SmsCodeSent = { sentTo: string; expiresAt: string; resendAfterSeconds: number };

// Confirming something with a code sent by SMS: the person asks for a code, the recipient reads it out, and it is entered here.
// The component only runs that conversation (the send button and its wait, the code box, the check). What is sent, and what a right
// code means, are the caller's: it gives a function that sends and a function that checks, so the same component serves any
// confirmation by SMS. Errors are shown by the app's error toast.
@Component({
  selector: 'sms-code-confirm',
  imports: [...SharedMaterialUIModule],
  templateUrl: './sms-code-confirm.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SmsCodeConfirmComponent {
  readonly title = input('Confirm with an SMS code');
  readonly description = input('');
  readonly sendLabel = input('Send a code');
  readonly verifiedText = input('The code was right.');
  // The caller says when sending is allowed (when the details it needs are there).
  readonly canSend = input(false);
  readonly readOnly = input(false);
  readonly send = input.required<() => Observable<SmsCodeSent>>();
  readonly verify = input.required<(code: string) => Observable<unknown>>();
  readonly verified = output<void>();

  protected readonly busy = signal(false);
  protected readonly info = signal<string | null>(null);
  protected readonly sentTo = signal<string | null>(null);
  protected readonly resendIn = signal(0);
  protected readonly code = signal('');
  protected readonly canVerify = computed(() => !this.busy() && /^\d{4,10}$/.test(this.code().trim()));

  private resendTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopCountdown());
  }

  protected sendCode(): void {
    if (!this.canSend() || this.busy() || this.resendIn() > 0) {
      return;
    }

    this.busy.set(true);
    this.info.set(null);

    this.send()().subscribe({
      next: (sent) => {
        this.busy.set(false);
        this.sentTo.set(sent.sentTo);
        this.info.set(`A code was sent to ${sent.sentTo}. It works until ${this.timeOf(sent.expiresAt)}.`);
        this.startCountdown(sent.resendAfterSeconds);
      },
      error: () => this.busy.set(false),
    });
  }

  protected verifyCode(): void {
    if (!this.canVerify()) {
      return;
    }

    this.busy.set(true);
    this.verify()(this.code().trim()).subscribe({
      next: () => {
        this.busy.set(false);
        this.sentTo.set(null);
        this.code.set('');
        this.info.set(this.verifiedText());
        this.stopCountdown();
        this.verified.emit();
      },
      error: () => this.busy.set(false),
    });
  }

  protected codeTyped(event: Event): void {
    this.code.set((event.target as HTMLInputElement).value);
  }

  // The time of a server time, in the person's own time.
  private timeOf(value: string): string {
    return parseServerUtc(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private startCountdown(seconds: number): void {
    this.stopCountdown();
    this.resendIn.set(seconds);
    this.resendTimer = setInterval(() => {
      this.resendIn.update((left) => Math.max(0, left - 1));
      if (this.resendIn() === 0) {
        this.stopCountdown();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }

    this.resendIn.set(0);
  }
}
